import { useContext, useMemo } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { gql, useMutation, useQuery } from '@apollo/client';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useQuery as useReactQuery } from '@tanstack/react-query';
import { getConfig } from '@edx/frontend-platform';
import { getAuthenticatedHttpClient } from '@edx/frontend-platform/auth';
import { AppContext } from '@edx/frontend-platform/react';

// Friendly fallback label per component type (used when no real title resolves).
const TYPE_LABEL = {
  course: 'Course',
  video: 'Video',
  assessment: 'Assessment',
  text: 'Reading',
  material: 'Material',
  live_session: 'Live session',
  feedback: 'Feedback',
  role_play: 'Role play',
};

// Resolve library-block display names via blendxapi (modulestore-backed, so it
// works for learners who aren't staff on the competency library course).
const fetchComponentNames = async (blockIds) => {
  if (!blockIds.length) { return {}; }
  const { data } = await getAuthenticatedHttpClient().post(
    `${getConfig().LMS_BASE_URL}/blendxapi/learning-pathway/component-names/`,
    { block_ids: blockIds },
    { headers: { 'content-type': 'application/json' } },
  );
  return data?.names || {};
};

const GET_PATHWAY_DETAIL = gql`
  query FetchPathwayDetail($id: Uuid!) {
    learningPathways(where: { id: { _eq: $id } }) {
      id
      name
      description
      image
      isSequential
      isSelfPaced
      duration
      durationMetric
      isActive
      learningPathwayComponents(order_by: { sequenceOrder: Asc }) {
        id
        contentId
        componentType
        isMandatory
        sequenceOrder
      }
    }
  }
`;

// Course title + image for course components, via the learner-accessible
// _CourseOverviewsCourseoverview relationship (same path the discover page uses).
const GET_PATHWAY_COURSE_DETAILS = gql`
  query FetchDiscoverPathwayCourseDetails($ids: [Text!]!) {
    courses(where: { openedxCourseId: { _in: $ids } }) {
      openedxCourseId
      details: _CourseOverviewsCourseoverview {
        displayName
        courseImageUrl
      }
    }
  }
`;

const IS_ENROLLED_IN_PATHWAY = gql`
  query IsEnrolledInPathway($userId: Int4!, $pathwayId: Uuid!) {
    learningPathwayEnrollments(
      where: { userId: { _eq: $userId }, pathwayId: { _eq: $pathwayId } }
    ) {
      id
    }
  }
`;

const ENROLL_IN_PATHWAY = gql`
  mutation EnrollInPathway($objects: [V2InsertLearningPathwayEnrollmentsObjectInput!]!) {
    v2InsertLearningPathwayEnrollments(objects: $objects) {
      affectedRows
    }
  }
`;

// Learner self-enroll always starts today; end date derives from the pathway's
// own duration (parity with the staff enrollment flow).
const computeDates = ({ duration, durationMetric }) => {
  const start = new Date();
  const startDate = start.toISOString();
  if (!duration || !durationMetric) {
    return { startDate, endDate: null };
  }
  const end = new Date(start);
  if (durationMetric === 'days') {
    end.setDate(end.getDate() + duration);
  } else if (durationMetric === 'weeks') {
    end.setDate(end.getDate() + duration * 7);
  } else if (durationMetric === 'months') {
    end.setMonth(end.getMonth() + duration);
  }
  return { startDate, endDate: end.toISOString() };
};

export const usePathwayDiscover = (pathwayId) => {
  const { authenticatedUser } = useContext(AppContext);
  const userId = authenticatedUser?.userId ?? authenticatedUser?.id;

  const {
    data, loading, error, refetch,
  } = useQuery(GET_PATHWAY_DETAIL, {
    variables: { id: pathwayId },
    skip: !pathwayId,
  });

  const { data: enrolledData, refetch: refetchEnrolled } = useQuery(IS_ENROLLED_IN_PATHWAY, {
    variables: { userId, pathwayId },
    skip: !userId || !pathwayId,
    fetchPolicy: 'cache-and-network',
  });

  const [enrollMutation, { loading: enrolling }] = useMutation(ENROLL_IN_PATHWAY);

  const pathway = data?.learningPathways?.[0] || null;
  const isEnrolled = (enrolledData?.learningPathwayEnrollments?.length || 0) > 0;

  const rawComponents = useMemo(
    () => pathway?.learningPathwayComponents ?? [],
    [pathway],
  );

  // Course titles + images (GraphQL).
  const courseIds = useMemo(
    () => rawComponents.filter((c) => c.componentType === 'course').map((c) => c.contentId),
    [rawComponents],
  );
  const { data: courseData } = useQuery(GET_PATHWAY_COURSE_DETAILS, {
    variables: { ids: courseIds },
    skip: !courseIds.length,
    fetchPolicy: 'cache-and-network',
  });
  const courseDetailById = useMemo(() => {
    const map = {};
    (courseData?.courses ?? []).forEach((c) => {
      const d = c.details;
      map[c.openedxCourseId] = { title: d?.displayName || null, image: d?.courseImageUrl || null };
    });
    return map;
  }, [courseData]);

  // Library-block names via blendxapi (modulestore-backed).
  const blockIds = useMemo(() => [...new Set(
    rawComponents
      .filter((c) => c.componentType !== 'course')
      .map((c) => c.contentId),
  )].sort(), [rawComponents]);
  const { data: blockNames = {} } = useReactQuery({
    queryKey: ['discoverPathwayBlockNames', blockIds],
    queryFn: () => fetchComponentNames(blockIds),
    enabled: blockIds.length > 0,
  });

  const components = useMemo(() => rawComponents.map((c) => {
    const courseDetail = courseDetailById[c.contentId];
    return {
      ...c,
      displayName: courseDetail?.title
        || blockNames[c.contentId]
        || TYPE_LABEL[c.componentType]
        || c.contentId,
      imageUrl: courseDetail?.image || null,
    };
  }), [rawComponents, courseDetailById, blockNames]);

  const enroll = async () => {
    if (!pathway || !userId) { return; }
    const { startDate, endDate } = computeDates({
      duration: pathway.duration,
      durationMetric: pathway.durationMetric,
    });
    await enrollMutation({
      variables: {
        objects: [{
          pathwayId: pathway.id,
          userId,
          enrolledBy: userId,
          status: 'enrolled',
          startDate,
          endDate,
        }],
      },
    });
    await refetchEnrolled();
  };

  return {
    pathway,
    components,
    isEnrolled,
    enroll,
    enrolling,
    loading,
    error,
    refetch,
  };
};
